<script setup lang="ts">
import DiagnosticSection from '@/components/video-diagnostic/DiagnosticSection.vue'
import TipsQualityBox from '@/components/video-diagnostic/TipsQualityBox.vue'
import { useVideoDiagnostic } from '@/composables/zoom-diagnostic/useVideoDiagnostic'
import CommonState from '@/services/locale-state'
import { Stage, Topic } from '@/types/enums/zoom-diagnostic.enum'
import { IUpdateTopic } from '@/types/interfaces/video-diagnostic.interface'
import { watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { videoInputTestPassed } from '@/composables/zoom-diagnostic/useCameraTest'
import { audioTestPassed } from '@/composables/zoom-diagnostic/useAudioTest'
import { audioTestPassed as audioTestPassedRoom } from '@/composables/video-diagnostic/useAudioTest'
import { connectionFailed } from '@/composables/zoom-diagnostic/useConnection'
import { isIOSMobile } from '@/helpers/zoom-device.helper'

interface ResultEmits {
  (e: 'nextStage', value: number): void
  (e: 'updateTopic', value: IUpdateTopic): void
}

const router = useRouter()
const route = useRoute()
const { sendReport, isLoading } = useVideoDiagnostic()

const props = defineProps({
  stage: {
    type: Number,
    required: true,
  },
})

const supportTel = computed(() =>
  CommonState.getCountry() === 'vn' ? '0906.830.230' : '02-026-6683',
)

const audioTest = computed(() =>
  isIOSMobile() ? audioTestPassedRoom.value : audioTestPassed.value,
)

const emit = defineEmits<ResultEmits>()

watch(
  () => props.stage,
  async (stage, prevStage) => {
    if (prevStage === Stage.RESULTS) {
      emit('updateTopic', { topic: Topic.RESULTS, active: false })
    }
    if (stage === Stage.RESULTS) {
      emit('updateTopic', { topic: Topic.RESULTS, active: true })
    }
  },
)

const testsPassed = computed(
  () => videoInputTestPassed.value && audioTest.value && !connectionFailed.value,
)

const refreshPage = () => {
  router.go(0)
}

const backToFirstPage = () => {
  router.go(-1)
}
</script>

<template>
  <DiagnosticSection
    :id="Stage.RESULTS"
    :class="stage === Stage.RESULTS ? 'block' : 'hidden md:block opacity-0 pointer-events-none'"
    class="result-container"
  >
    <template #default>
      <div class="row-span-2 text-sm sm:text-base lg:text-sm xl:text-base">
        <h1 class="font-bold text-xl sm:text-4xl lg:text-xl xl:text-4xl">
          {{
            testsPassed
              ? $t('zoom-diagnostic.all-test-passed')
              : $t('zoom-diagnostic.some-test-failed')
          }}
        </h1>
        <p v-if="testsPassed" class="py-4 sm:py-10 lg:py-4 xl:py-10">
          {{ $t('zoom-diagnostic.test-past-detail') }}
        </p>
        <p v-else class="py-4 sm:py-10 lg:py-4 xl:py-10">
          <span class="font-bold">{{ $t('zoom-diagnostic.some-test-failed') }} </span>

          {{ $t('zoom-diagnostic.test-failed-detail') }}
          <span v-if="route.query.exitPath">
            {{ $t('zoom-diagnostic.test-failed-detail2', { tel: supportTel }) }}
          </span>
        </p>
        <TipsQualityBox v-if="!testsPassed" />
        <div class="flex py-6">
          <button
            v-if="route.query.exitPath"
            class="btn-submit"
            type="button"
            :disabled="isLoading"
            @click="sendReport"
          >
            <div class="flex">
              <Loading class="mr-2" :is-loading="isLoading" />
              {{ $t('zoom-diagnostic.finish') }}
            </div>
          </button>
          <button
            v-else
            class="text-white border-2 rounded-md border-white bg-primary-color py-2 px-4 mr-4"
            type="button"
            @click="backToFirstPage"
          >
            {{ $t('zoom-diagnostic.back') }}
          </button>
          <button class="bg-white border py-2 px-4 rounded-md" type="button" @click="refreshPage">
            {{ $t('zoom-diagnostic.restart-test') }}
          </button>
        </div>
      </div>

      <div class="row-span-2 text-sm sm:text-base lg:text-sm xl:text-base">
        <div class="max-w-sm border-b-2 mb-6">
          <h2 class="font-bold text-xl">
            <SvgIcon
              :class="videoInputTestPassed ? 'text-green-500' : ''"
              class="icon-container mr-2"
              :icon="videoInputTestPassed ? 'correct-circle' : 'error-sign'"
              :inherit-fill="true"
              height="16"
            />
            {{ $t('zoom-diagnostic.section-camera.header') }}
          </h2>
          <p
            class="py-4"
            v-html="
              videoInputTestPassed
                ? $t('zoom-diagnostic.section-camera.passed-message')
                : $t('zoom-diagnostic.section-camera.failed-message')
            "
          ></p>
        </div>
        <div class="max-w-sm border-b-2 mb-6">
          <h2 class="font-bold text-xl">
            <SvgIcon
              :class="audioTest ? 'text-green-500' : ''"
              class="icon-container mr-2"
              :icon="audioTest ? 'correct-circle' : 'error-sign'"
              :inherit-fill="true"
              height="16"
            />
            {{ $t('zoom-diagnostic.section-speaker-and-mic.header') }}
          </h2>
          <p
            class="py-4"
            v-html="
              audioTest
                ? $t('zoom-diagnostic.section-speaker-and-mic.passed-message')
                : $t('zoom-diagnostic.section-speaker-and-mic.failed-message')
            "
          ></p>
        </div>
        <div class="max-w-sm border-b-2 mb-6">
          <h2 class="font-bold text-xl">
            <SvgIcon
              class="icon-container text-green-500 mr-2"
              :icon="'correct-circle'"
              :inherit-fill="true"
              height="16"
            />
            {{ $t('zoom-diagnostic.connectivity-result') }}
          </h2>
          <p class="py-4" v-html="$t('zoom-diagnostic.connectivity-detail')"></p>
        </div>
      </div>
    </template>
  </DiagnosticSection>
</template>

<style lang="scss" scoped>
.bg-primary-color {
  background-color: #f67519;
}

button.bg-white {
  &:hover {
    background-color: #32323209;
    transition: 0.4s;
  }
  &:disabled {
    background-color: inherit;
    color: rgba(0, 0, 0, 0.26);
  }
}

.h-fit {
  height: fit-content;
}

.result-container {
  padding-bottom: 10rem;
}

@include media-breakpoint-down-custom(1080px) {
  .result-container {
    overflow-y: auto;
    padding: 6rem 5rem;
  }
}

@include media-breakpoint-down-custom(640px) {
  .result-container {
    overflow-y: auto;
    padding: 2rem 1.5rem;
  }
}

.btn-submit {
  @apply text-white border-2 rounded-md border-white py-2 px-4 mr-4;
  @extend .bg-primary-color;
  &:disabled {
    @apply bg-gray-400;
  }
}
::v-deep(.icon-container svg) {
  display: inline-block !important;
  margin-bottom: 0.7rem;
  width: 16px;
  height: 16px;
}
</style>
